<script setup lang="ts">
import { Bell, ChevronDown, ExternalLink, LogOut } from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import ThemeToggle from '@/components/ThemeToggle.vue';
import { useMyNotificationsQuery } from '@/queries/notifications.js';
import { useAuthStore } from '@/stores/auth.js';

interface NavLink {
  to: { name: string };
  label: string;
}

const { t } = useI18n();
const auth = useAuthStore();

const navLinks = computed<NavLink[]>(() => [
  { to: { name: 'events' }, label: t('nav.events') },
  // Управление администраторами видно только администратору: право выдать
  // себе подобного — самое сильное на платформе.
  ...(auth.isPlatformAdmin
    ? [{ to: { name: 'platform-admins' }, label: t('nav.platformAdmins') }]
    : []),
]);

/** Счётчик непрочитанных: без него уведомления никто не открывает. */
const notifications = useMyNotificationsQuery();
const unreadCount = computed(
  () => (notifications.data.value ?? []).filter((n) => !n.readAt).length,
);

const lkUrl = import.meta.env.VITE_LK_URL ?? 'http://localhost:5173';

const userInitials = computed(() => {
  const name = auth.user?.displayName ?? auth.user?.email ?? '';
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
});

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);

function closeMenu(returnFocus = false) {
  menuOpen.value = false;
  // Фокус возвращаем на кнопку: иначе после Escape он улетает в начало
  // документа и клавиатурный пользователь теряет место.
  if (returnFocus) triggerRef.value?.focus();
}

function handleDocumentClick(event: MouseEvent) {
  if (!menuOpen.value) return;
  const target = event.target as Node | null;
  if (menuRef.value && target && !menuRef.value.contains(target)) closeMenu();
}

function handleKey(event: KeyboardEvent) {
  if (event.key === 'Escape' && menuOpen.value) closeMenu(true);
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKey);
});
</script>

<template>
  <div class="shell">
    <a class="skip-link" href="#main">{{ t('shell.skipToContent') }}</a>

    <header class="shell__topbar">
      <RouterLink :to="{ name: 'events' }" class="shell__brand">
        <svg class="shell__logo" viewBox="0 0 32 32" aria-hidden="true">
          <defs>
            <linearGradient
              id="ev-shell-logo"
              x1="0"
              y1="0"
              x2="32"
              y2="32"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stop-color="#3B4CBD" />
              <stop offset="1" stop-color="#6366F1" />
            </linearGradient>
          </defs>
          <rect width="32" height="32" rx="8" fill="url(#ev-shell-logo)" />
          <rect x="7" y="9" width="18" height="16" rx="3" fill="#FFFFFF" />
          <rect x="11" y="6" width="2.5" height="5" rx="1.25" fill="#FFFFFF" />
          <rect x="18.5" y="6" width="2.5" height="5" rx="1.25" fill="#FFFFFF" />
          <circle cx="12" cy="18" r="1.6" fill="#3B4CBD" />
          <circle cx="16" cy="18" r="1.6" fill="#3B4CBD" fill-opacity="0.45" />
          <circle cx="20" cy="18" r="1.6" fill="#3B4CBD" fill-opacity="0.45" />
        </svg>
        <span class="shell__brand-name">{{ t('app.name') }}</span>
      </RouterLink>

      <nav class="shell__nav" :aria-label="t('shell.mainNav')">
        <RouterLink
          v-for="link in navLinks"
          :key="link.to.name"
          :to="link.to"
          class="shell__nav-link"
          active-class="shell__nav-link--active"
        >
          {{ link.label }}
        </RouterLink>
      </nav>

      <div class="shell__actions">
        <a class="shell__lk-link" :href="lkUrl">
          <ExternalLink :size="14" :stroke-width="1.75" aria-hidden="true" />
          <span class="shell__lk-label">{{ t('shell.toLk') }}</span>
        </a>

        <RouterLink
          :to="{ name: 'notifications' }"
          class="shell__bell"
          :aria-label="t('notifications.title')"
        >
          <Bell :size="18" :stroke-width="1.75" aria-hidden="true" />
          <span v-if="unreadCount" class="shell__badge">{{ unreadCount }}</span>
        </RouterLink>

        <ThemeToggle />

        <div ref="menuRef" class="shell__user">
          <button
            ref="triggerRef"
            type="button"
            class="shell__user-trigger"
            :aria-expanded="menuOpen"
            :aria-label="t('shell.userMenu')"
            aria-haspopup="menu"
            @click="menuOpen = !menuOpen"
          >
            <span class="shell__avatar" aria-hidden="true">{{ userInitials }}</span>
            <span class="shell__user-meta">
              <span class="shell__user-name">{{ auth.user?.displayName ?? '—' }}</span>
              <span class="shell__user-email">{{ auth.user?.email ?? '' }}</span>
            </span>
            <ChevronDown :size="16" :stroke-width="1.75" class="shell__caret" aria-hidden="true" />
          </button>

          <div v-if="menuOpen" class="shell__dropdown" role="menu">
            <button
              type="button"
              class="shell__dropdown-item shell__dropdown-item--danger"
              role="menuitem"
              @click="
                closeMenu();
                void auth.logout();
              "
            >
              <LogOut :size="16" :stroke-width="1.75" aria-hidden="true" />
              <span>{{ t('shell.logout') }}</span>
            </button>
          </div>
        </div>
      </div>
    </header>

    <main id="main" class="shell__content">
      <slot />
    </main>
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  min-height: 100vh;
  flex-direction: column;
  background: var(--color-bg-surface);
}

.shell__topbar {
  position: sticky;
  top: 0;
  z-index: var(--z-dropdown);
  display: flex;
  height: 60px;
  align-items: center;
  gap: var(--space-4);
  padding: 0 var(--space-5);
  border-bottom: 1px solid var(--color-border-subtle);
  background: var(--color-bg-translucent);
  backdrop-filter: blur(10px);
}

.shell__brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  color: var(--color-text);
  text-decoration: none;
}
.shell__brand:hover {
  text-decoration: none;
}

.shell__logo {
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.shell__brand-name {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  letter-spacing: var(--letter-spacing-tight);
}

.shell__nav {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-left: var(--space-4);
}

.shell__nav-link {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}
.shell__nav-link:hover {
  background: var(--color-bg-muted);
  color: var(--color-text);
  text-decoration: none;
}
.shell__nav-link--active {
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.shell__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-left: auto;
}

.shell__lk-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  font-size: var(--font-size-sm);
  text-decoration: none;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}
.shell__lk-link:hover {
  background: var(--color-bg-muted);
  color: var(--color-text);
  text-decoration: none;
}

.shell__bell {
  position: relative;
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
}
.shell__bell:hover {
  background: var(--color-bg-muted);
  color: var(--color-text);
  text-decoration: none;
}

.shell__badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  padding: 0 4px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
}

.shell__user {
  position: relative;
}

.shell__user-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1) var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  transition: background var(--transition-fast);
}
.shell__user-trigger:hover {
  background: var(--color-bg-muted);
}

.shell__avatar {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: var(--radius-full);
  background: var(--gradient-brand);
  color: #fff;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.shell__user-meta {
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  line-height: var(--line-height-tight);
}

.shell__user-name {
  max-width: 180px;
  overflow: hidden;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell__user-email {
  max-width: 180px;
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shell__caret {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.shell__dropdown {
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  z-index: var(--z-dropdown);
  min-width: 200px;
  padding: var(--space-1);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-lg);
  background: var(--color-bg);
  box-shadow: var(--shadow-lg);
}

.shell__dropdown-item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: none;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text);
  font-family: inherit;
  font-size: var(--font-size-base);
  text-align: left;
  cursor: pointer;
}
.shell__dropdown-item:hover {
  background: var(--color-bg-muted);
}
.shell__dropdown-item--danger {
  color: var(--color-danger);
}

.shell__content {
  flex: 1;
}

/* Узкие экраны: оставляем только то, что нельзя убрать. Имя и почта
   пользователя прячутся — их видно в открытом меню, а место в топбаре
   стоит дороже. */
@media (max-width: 767px) {
  .shell__topbar {
    padding: 0 var(--space-4);
    gap: var(--space-2);
  }
  .shell__brand-name,
  .shell__user-meta,
  .shell__lk-label,
  .shell__caret {
    display: none;
  }
  .shell__nav {
    margin-left: var(--space-1);
  }
}
</style>
